import React, { useEffect, useState } from 'react';
import {
  Card, Table, Typography, Button, Space, Descriptions, message, Spin, Tag,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  OneCServiceOrderDetail,
  getOneCServiceOrder,
} from '../api/onec';

const { Title, Text } = Typography;

const fmtDate = (v: string | null | undefined) =>
  v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—';

const fmtMoney = (v: number | null | undefined) =>
  v != null ? Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽' : '—';

const fmtNum = (v: number | null | undefined, digits = 3) =>
  v != null ? Number(v).toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : '—';

/** Отобразить значение Descriptions.Item только если оно не пустое */
const DescItem: React.FC<{ label: string; children: React.ReactNode; span?: number }> = ({ label, children, span }) => {
  const text = typeof children === 'string' ? children : '';
  if (text === '—' || text === '' || text == null) return null;
  return <Descriptions.Item label={label} span={span}>{children}</Descriptions.Item>;
};

const OneCServiceOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OneCServiceOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getOneCServiceOrder(id)
      .then(setOrder)
      .catch((e: any) => {
        message.error(e?.response?.data?.detail || 'Не удалось загрузить заказ из 1С');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const productColumns = [
    { title: '№', dataIndex: 'line_number', key: 'line_number', width: 50 },
    { title: 'Артикул', dataIndex: 'article', key: 'article', width: 120, render: (v: string) => v || '—' },
    { title: 'Номенклатура', dataIndex: 'nomenclature', key: 'nomenclature', render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Ед.', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: 'Кол-во', dataIndex: 'quantity', key: 'quantity', width: 90, align: 'right' as const, render: (v: number) => fmtNum(v, 3) },
    { title: 'Цена', dataIndex: 'price', key: 'price', width: 110, align: 'right' as const, render: (v: number) => fmtMoney(v) },
    { title: 'Сумма', dataIndex: 'sum', key: 'sum', width: 110, align: 'right' as const, render: (v: number) => <Text strong>{fmtMoney(v)}</Text> },
    { title: 'НДС', dataIndex: 'vat', key: 'vat', width: 100, align: 'right' as const, render: (v: number) => fmtMoney(v) },
    { title: 'Ставка НДС', dataIndex: 'vat_rate', key: 'vat_rate', width: 100 },
  ];

  const serviceColumns = [
    { title: '№', dataIndex: 'line_number', key: 'line_number', width: 50 },
    { title: 'Услуга', dataIndex: 'service_name', key: 'service_name', render: (v: string, r: any) => <Text strong>{v || r.service_catalog}</Text> },
    { title: 'Кол-во', dataIndex: 'quantity', key: 'quantity', width: 80, align: 'right' as const, render: (v: number) => fmtNum(v, 3) },
    { title: 'Норма час', dataIndex: 'norm_hour', key: 'norm_hour', width: 100, align: 'right' as const, render: (v: number) => fmtNum(v, 2) },
    { title: 'Сумма', dataIndex: 'sum', key: 'sum', width: 110, align: 'right' as const, render: (v: number) => <Text strong>{fmtMoney(v)}</Text> },
    { title: 'НДС', dataIndex: 'vat', key: 'vat', width: 100, align: 'right' as const, render: (v: number) => fmtMoney(v) },
    { title: 'Ставка НДС', dataIndex: 'vat_rate', key: 'vat_rate', width: 100 },
    { title: 'Исполнитель', dataIndex: 'executor1', key: 'executor1', width: 160 },
  ];

  const relatedDocColumns = [
    { title: '№', dataIndex: 'line_number', key: 'line_number', width: 50 },
    { title: 'Документ реализации', dataIndex: 'sales_doc', key: 'sales_doc' },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/1c-service-orders')}>
          Назад к списку
        </Button>
        <div style={{ marginTop: 24 }}>Заказ не найден</div>
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/1c-service-orders')}>
          Назад
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          Заказ-наряд {order.number}
        </Title>
        <Tag color="blue">{order.status}</Tag>
      </Space>

      {/* ── Основное ── */}
      <Card size="small" title="Основное" style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <DescItem label="Дата">{fmtDate(order.date)}</DescItem>
          <DescItem label="Статус">{order.status}</DescItem>
          <DescItem label="Сумма"><Text strong>{fmtMoney(order.amount)}</Text></DescItem>
          <DescItem label="Контрагент">{order.counterparty}</DescItem>
          <DescItem label="ФИО розн. покупателя">{order.retail_buyer_name}</DescItem>
          <DescItem label="Организация">{order.organization}</DescItem>
          <DescItem label="Оплата картой">{order.card_payment ? 'Да' : 'Нет'}</DescItem>
          <DescItem label="Вид оплаты">{order.payment_type}</DescItem>
          <DescItem label="Предоплата">{fmtMoney(order.prepayment_sum)}</DescItem>
          <DescItem label="Пробивать по ККТ">{order.print_receipt ? 'Да' : 'Нет'}</DescItem>
          <DescItem label="НДС (товары)">{order.vat_for_products ? 'Да' : 'Нет'}</DescItem>
          <DescItem label="НДС (услуги)">{order.vat_for_services ? 'Да' : 'Нет'}</DescItem>
        </Descriptions>
      </Card>

      {/* ── Автомобиль ── */}
      {(order.vehicle || order.vin || order.license_plate) && (
        <Card size="small" title="Автомобиль" style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <DescItem label="Автомобиль">{order.vehicle}</DescItem>
            <DescItem label="VIN">{order.vin}</DescItem>
            <DescItem label="Гос. номер">{order.license_plate}</DescItem>
            <DescItem label="Номер кузова">{order.body_number}</DescItem>
            <DescItem label="Номер двигателя">{order.engine_number}</DescItem>
            <DescItem label="Тип двигателя">{order.engine_type}</DescItem>
            <DescItem label="Год выпуска">{order.manufacture_year}</DescItem>
            <DescItem label="Цвет">{order.color}</DescItem>
            <DescItem label="Пробег">{order.mileage != null ? fmtNum(order.mileage, 0) + ' км' : '—'}</DescItem>
            <DescItem label="Дата покупки">{fmtDate(order.purchase_date)}</DescItem>
            <DescItem label="Стоимость ремонта">{fmtMoney(order.repair_cost)}</DescItem>
          </Descriptions>
        </Card>
      )}

      {/* ── Работы ── */}
      {(order.work_start_date || order.work_end_date || order.recommendations || order.norm_hour) && (
        <Card size="small" title="Работы" style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <DescItem label="Дата начала работ">{fmtDate(order.work_start_date)}</DescItem>
            <DescItem label="Дата конца работ">{fmtDate(order.work_end_date)}</DescItem>
            <DescItem label="Норма час">{fmtNum(order.norm_hour, 2)}</DescItem>
            <DescItem label="Нормативный документ">{order.regulatory_doc}</DescItem>
            <DescItem label="Особые отметки">{order.special_notes}</DescItem>
            <DescItem label="Рекомендации">{order.recommendations}</DescItem>
          </Descriptions>
        </Card>
      )}

      {/* ── Страховка ── */}
      {order.insurer && (
        <Card size="small" title="Страховка" style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <DescItem label="Страхователь">{order.insurer}</DescItem>
          </Descriptions>
        </Card>
      )}

      {/* ── Товары ── */}
      {(order.products?.length || 0) > 0 && (
        <Card size="small" title="Товары" style={{ marginBottom: 16 }}>
          <Table
            dataSource={order.products}
            columns={productColumns}
            rowKey={(r: any) => `p-${r.line_number}`}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </Card>
      )}

      {/* ── Услуги ── */}
      {(order.services?.length || 0) > 0 && (
        <Card size="small" title="Услуги" style={{ marginBottom: 16 }}>
          <Table
            dataSource={order.services}
            columns={serviceColumns}
            rowKey={(r: any) => `s-${r.line_number}`}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </Card>
      )}

      {/* ── Связанные документы ── */}
      {(order.related_docs?.length || 0) > 0 && (
        <Card size="small" title="Связанные документы" style={{ marginBottom: 16 }}>
          <Table
            dataSource={order.related_docs}
            columns={relatedDocColumns}
            rowKey={(r: any) => `d-${r.line_number}`}
            pagination={false}
            size="small"
          />
        </Card>
      )}
    </div>
  );
};

export default OneCServiceOrderDetailPage;
